import { Component, Input, OnChanges, AfterViewInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
// @ts-ignore
import * as CanvasJS from "./canvasjs.stock.min.js"

@Component({
  selector: 'app-trends-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="chart-wrapper">
      <div [id]="chartContainerId" style="height: 450px; width: 100%;"></div>
    </div>
  `,
  styles: [`
    .chart-wrapper {
      width: 100%;
      padding: 10px;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
  `]
})
export class TrendsChartComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() dataSeries: any[] = [];
  @Input() titleText: string = '';
  @Input() titleX: string = 'Time';
  @Input() titleY: string = 'Value';
  @Input() thresholds: { green?: number; yellow?: number; red?: number } = {};
  @Input() yAxisFormat: string = '#0.#####';

  public chartContainerId = 'trendChartContainer-' + Math.floor(Math.random() * 100000);
  private chart: any;

  ngAfterViewInit() {
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.chart && (changes['dataSeries'] || changes['thresholds'])) {
      this.updateChart();
    }
  }

  // Detect if the chart is using date-based or numeric X-axis
  private isDateBasedChart(): boolean {
    if (!this.dataSeries || this.dataSeries.length === 0) return true; // default to date

    // Check the first series for xValueType
    const firstSeries = this.dataSeries[0];
    if (firstSeries.xValueType === 'number') return false;
    if (firstSeries.xValueType === 'dateTime') return true;

    // Fallback: Check first dataPoint
    if (firstSeries.dataPoints && firstSeries.dataPoints.length > 0) {
      const firstPoint = firstSeries.dataPoints[0];
      return firstPoint.x instanceof Date;
    }

    return true; // default to date if uncertain
  }

  // Helper to generate StripLines (Background Zones)
  private getStripLines() {
    const stripLines: any[] = [];
    const t = this.thresholds;

    // 1. Green Zone (0 to Green)
    if (t.green !== undefined) {
      stripLines.push({
        startValue: 0,
        endValue: t.green,
        color: "rgba(0, 227, 150, 0.1)",
        label: "Healthy",
        labelFontColor: "#00a854",
        labelAlign: "far",
        labelVerticalAlign: "bottom"
      });

      stripLines.push({
        value: t.green,
        color: "#00a854",
        thickness: 2,
        lineDashType: "dot"
      });
    }

    // 2. Yellow Zone (Green to Yellow)
    if (t.green !== undefined && t.yellow !== undefined) {
      stripLines.push({
        startValue: t.green,
        endValue: t.yellow,
        color: "rgba(254, 176, 25, 0.1)",
        label: "Unsatisfactory",
        labelFontColor: "#faad14",
        labelAlign: "far",
        labelVerticalAlign: "center"
      });

      stripLines.push({
        value: t.yellow,
        color: "#faad14",
        thickness: 2,
        lineDashType: "dot"
      });
    }

    // 3. Red Zone (Yellow to Red/Infinity)
    if (t.yellow !== undefined && t.red !== undefined) {
      stripLines.push({
        startValue: t.yellow,
        endValue: t.red * 1.5,
        color: "rgba(255, 69, 96, 0.1)",
        label: "Unacceptable",
        labelFontColor: "#cf1322",
        labelAlign: "far",
        labelVerticalAlign: "top"
      });

      stripLines.push({
        value: t.red,
        color: "#cf1322",
        thickness: 2,
        lineDashType: "dash"
      });
    }

    return stripLines;
  }

  renderChart() {
    const isDateBased = this.isDateBasedChart();

    // Dynamic axis X configuration based on data type
    const axisXConfig: any = {
      title: this.titleX,
      titleFontSize: 14,
      labelFontSize: 12,
      labelAngle: -45,
      crosshair: { enabled: true, snapToDataPoint: true },
      gridThickness: 1,
      gridColor: "#e0e0e0"
    };

    // Only add date formatting if it's a date-based chart
    if (isDateBased) {
      axisXConfig.valueFormatString = "DD MMM HH:mm";
    } else {
      // For numeric charts, use appropriate number formatting
      axisXConfig.valueFormatString = "#,##0.##";
    }

    const options = {
      theme: "light2",
      animationEnabled: true,
      zoomEnabled: true,
      exportEnabled: true,
      title: {
        text: this.titleText,
        fontSize: 20,
        fontFamily: "Arial, sans-serif"
      },
      axisX: axisXConfig,
      axisY: {
        title: this.titleY,
        titleFontSize: 14,
        labelFontSize: 12,
        includeZero: false,
        valueFormatString: this.yAxisFormat,
        crosshair: { enabled: true },
        gridThickness: 1,
        gridColor: "#e0e0e0",
        stripLines: this.getStripLines()
      },
      toolTip: {
        shared: true,
        contentFormatter: (e: any) => {
          let content = '';

          // Format X-axis value based on type
          if (isDateBased) {
            content = `<strong>${new Date(e.entries[0].dataPoint.x).toLocaleString()}</strong><br/>`;
          } else {
            const xValue = e.entries[0].dataPoint.x;
            content = `<strong>${xValue.toFixed(2)} Hz</strong><br/>`;
          }

          e.entries.forEach((entry: any) => {
            const color = entry.dataSeries.color || '#000';
            const value = entry.dataPoint.y.toFixed(5);
            content += `<span style="color:${color}">${entry.dataSeries.name}</span>: ${value}<br/>`;
          });
          return content;
        }
      },
      legend: {
        cursor: "pointer",
        verticalAlign: "top",
        horizontalAlign: "center",
        dockInsidePlotArea: false,
        fontSize: 12,
        itemclick: (e: any) => {
          if (typeof (e.dataSeries.visible) === "undefined" || e.dataSeries.visible) {
            e.dataSeries.visible = false;
          } else {
            e.dataSeries.visible = true;
          }
          e.chart.render();
        }
      },
      data: this.dataSeries
    };

    // @ts-ignore
    this.chart = new CanvasJS.Chart(this.chartContainerId, options);
    this.chart.render();
  }

  updateChart() {
    if (this.chart) {
      const isDateBased = this.isDateBasedChart();

      // Update data
      this.chart.options.data = this.dataSeries;

      // Update axis X format dynamically
      if (isDateBased) {
        this.chart.options.axisX.valueFormatString = "DD MMM HH:mm";
      } else {
        this.chart.options.axisX.valueFormatString = "#,##0.##";
      }

      // Update strip lines
      this.chart.options.axisY.stripLines = this.getStripLines();

      this.chart.render();
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
